import { useState, useEffect, useRef } from 'react';
import { Bot, Send, Loader2, User, AlertCircle, Mail, MapPin, Clock, Users } from 'lucide-react';
import { db, collection, query, getDocs, where, doc, getDoc, UserProfile } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import OpenAI from 'openai';

const EXAMPLE_QUESTIONS = [
  {
    icon: Mail,
    question: "Donne-moi les informations de contact de [nom du contact] ?",
    description: "Obtenir les coordonnées complètes d'un contact"
  },
  {
    icon: MapPin,
    question: "Peux-tu me donner l'adresse de [nom de l'influenceur] ?",
    description: "Trouver l'adresse d'un influenceur"
  },
  {
    icon: Clock,
    question: "Combien de collaborations sont en attente de confirmation ?",
    description: "Voir les collaborations en attente"
  },
  {
    icon: Users,
    question: "Quels sont les meilleurs influenceurs pour une campagne de [secteur spécifique] ?",
    description: "Trouver les influenceurs adaptés à un secteur"
  }
];

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

interface AgencyUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

function Assistant() {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showExamples, setShowExamples] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agencyUsers, setAgencyUsers] = useState<AgencyUser[]>([]);
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const [contextData, setContextData] = useState<{
    clients: any[];
    collabs: any[];
    influencers: any[];
    castings: any[];
    totalRevenue: number;
    activeCollabs: number;
    completedCollabs: number;
    pendingCollabs: number;
    influencersByPlatform: Record<string, number>;
    topBrands: string[];
    resources: any[];
    todos: any[];
  }>({
    clients: [],
    collabs: [],
    influencers: [],
    castings: [],
    totalRevenue: 0,
    activeCollabs: 0,
    completedCollabs: 0,
    pendingCollabs: 0,
    influencersByPlatform: {},
    topBrands: [],
    resources: [],
    todos: []
  });
  const [userAgencyId, setUserAgencyId] = useState<string | null>(null);
  const [currentUserProfile, setCurrentUserProfile] = useState<UserProfile | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (currentUser) {
      fetchUserAgencyId();
    }
  }, [currentUser]);

  useEffect(() => {
    if (userAgencyId) {
      loadContextData();
      fetchAgencyUsers();
    }
  }, [userAgencyId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchAgencyUsers = async () => {
    if (!userAgencyId) return;

    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('agencyId', '==', userAgencyId)
      );
      const usersSnapshot = await getDocs(usersQuery);
      const users = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AgencyUser[];
      
      setAgencyUsers(users);

      const namesMap = users.reduce((acc, user) => ({
        ...acc,
        [user.id]: `${user.firstName} ${user.lastName}`
      }), {});
      setUserNames(namesMap);
    } catch (error) {
      console.error('Error fetching agency users:', error);
      setError('Erreur lors de la récupération des utilisateurs');
    }
  };

  const fetchUserAgencyId = async () => {
    if (currentUser && currentUser.uid) {
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDocSnapshot = await getDoc(userDocRef);

        if (userDocSnapshot.exists()) {
          const userData = userDocSnapshot.data() as UserProfile;
          setCurrentUserProfile(userData);
          const agencyId = userData.agencyId;
          if (agencyId) {
            setUserAgencyId(agencyId);
          } else {
            setError('L\'utilisateur n\'a pas d\'ID d\'agence associé.');
          }
        } else {
          setError('L\'utilisateur n\'existe pas dans la base de données.');
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        setError('Erreur lors de la récupération des données utilisateur.');
      }
    } else {
      setError('Utilisateur non authentifié.');
    }
  };

  const loadContextData = async () => {
    if (!userAgencyId) {
      setError('Aucune agence disponible pour cet utilisateur.');
      return;
    }

    try {
      const [clientsData, collabsData, influencersData, castingsData, resourcesData, todosData] = await Promise.all([
        getDocs(query(collection(db, 'clients'), where('agencyId', '==', userAgencyId))),
        getDocs(query(collection(db, 'collabs'), where('agencyId', '==', userAgencyId))),
        getDocs(query(collection(db, 'influencers'), where('agencyId', '==', userAgencyId), where('archived', '==', false))),
        getDocs(query(collection(db, 'castings'), where('agencyId', '==', userAgencyId))),
        getDocs(query(collection(db, 'resources'), where('agencyId', '==', userAgencyId))),
        getDocs(query(collection(db, 'todos'), where('agencyId', '==', userAgencyId))),
      ]);

      const todos = todosData.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(todo => 
          todo.userId === currentUser?.uid || 
          todo.assignedTo === currentUser?.uid
        );

      const clients = clientsData.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const collabs = collabsData.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const influencers = influencersData.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const castings = castingsData.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const resources = resourcesData.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const totalRevenue = collabs.reduce((total, collab) => {
        const collabTotal = collab.influencers?.reduce((sum, inf) =>
          sum + parseFloat(inf.rate || '0'), 0) || 0;
        return total + collabTotal;
      }, 0);

      const activeCollabs = collabs.filter(c => !c.payment_received && c.quote_sent).length;
      const completedCollabs = collabs.filter(c => c.payment_received).length;
      const pendingCollabs = collabs.filter(c => !c.quote_sent).length;

      const influencersByPlatform = influencers.reduce((acc, inf) => {
        inf.platforms.forEach((platform: any) => {
          acc[platform.name] = (acc[platform.name] || 0) + 1;
        });
        return acc;
      }, {} as Record<string, number>);

      const brandCounts = collabs.reduce((acc, collab) => {
        acc[collab.brand] = (acc[collab.brand] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const topBrands = Object.entries(brandCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
        .map(([brand]) => brand);

      setContextData({
        clients,
        collabs,
        influencers,
        castings,
        totalRevenue,
        activeCollabs,
        completedCollabs,
        pendingCollabs,
        influencersByPlatform,
        topBrands,
        resources,
        todos
      });
    } catch (error) {
      console.error('Error loading context data:', error);
      setError('Erreur lors du chargement des données');
    }
  };

  const formatDate = (date: any): string => {
    if (!date || !date.seconds) {
      return 'Date non disponible';
    }
    return new Date(date.seconds * 1000).toLocaleDateString('fr-FR');
  };

  const generateSystemMessage = () => {
    const {
      clients,
      collabs,
      influencers,
      castings,
      totalRevenue,
      activeCollabs,
      completedCollabs,
      pendingCollabs,
      influencersByPlatform,
      topBrands,
      todos
    } = contextData;
    
    return `Je suis Hosni, votre expert en marketing d'influence et conseiller spécialisé dans l'analyse des données d'agences d'influence. Je m'adresse à ${currentUserProfile?.firstName} ${currentUserProfile?.lastName} (${currentUserProfile?.role || 'Rôle non défini'}).

Vue d'ensemble de l'agence :
- Nombre total de clients : ${clients.length}
- Nombre total d'influenceurs : ${influencers.length}
- Chiffre d'affaires total : ${totalRevenue.toLocaleString('fr-FR')} €
- Collaborations : En cours (${activeCollabs}), Terminées (${completedCollabs}), En attente (${pendingCollabs})

Top 5 marques partenaires :
${topBrands.map((brand, index) => `${index + 1}. ${brand}`).join('\n')}

Répartition des influencers par plateforme :
${Object.entries(influencersByPlatform)
  .map(([platform, count]) => `- ${platform}: ${count} influenceurs`)
  .join('\n')}

Détails des clients (top 5) :
${clients.slice(0, 5).map(client => {
  const clientCollabs = collabs.filter(c => c.brand === client.brand_name);
  return `- ${client.brand_name}
  • Contacts: ${(client.contacts || []).slice(0, 2).map(c => 
    `${c.name} (${c.email}${c.phone ? `, ${c.phone}` : ''})`
  ).join(', ')}
  • Collaborations: ${clientCollabs.length}`;
}).join('\n')}

Influenceurs récents (top 5) :
${influencers.slice(0, 5).map(inf => {
  const platforms = inf.platforms.map((p: any) => `${p.name} (${p.audience})`).join(', ');
  return `- ${inf.fullName}
  • Plateformes: ${platforms}
  • Contact: ${inf.phoneNumber || 'Non renseigné'}`;
}).join('\n')}

Castings actifs (top 3) :
${castings.filter(c => c.status === 'active').slice(0, 3).map(casting => 
  `- ${casting.name} (${casting.brand})`
).join('\n')}

Tâches urgentes :
${todos.filter(todo => todo.status !== 'completed' && todo.priority === 'high').slice(0, 3).map(todo =>
  `- ${todo.title} (Échéance: ${formatDate(todo.dueDate)})`
).join('\n')}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = { role: 'user', content: input } as Message;
    setShowExamples(false);
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setError(null);

    try {
      const systemMessage = { role: 'system', content: generateSystemMessage() } as Message;
      
      const response = await openai.chat.completions.create({
        model: 'gpt-3.5-turbo',
        messages: [systemMessage, ...messages, userMessage],
        temperature: 0.8,
        max_tokens: 500
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.choices[0]?.message?.content || 'Désolé, je n\'ai pas pu générer une réponse.'
      } as Message;

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error generating response:', error);
      setError('Erreur lors de la génération de la réponse');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6 h-[calc(100vh-8rem)]">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center">
          <Bot className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-white">Hosni</h1>
          <p className="text-gray-400">Bonjour, je suis Hosni, votre expert en marketing d'influence. Comment puis-je vous aider aujourd'hui ?</p>
        </div>
      </div>

      <div className="bg-dark-50 rounded-xl border border-dark-200 flex flex-col h-full">
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {showExamples && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {EXAMPLE_QUESTIONS.map(({ icon: Icon, question, description }, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setInput(question);
                    handleSubmit(new Event('submit') as React.FormEvent);
                  }}
                  className="flex items-start gap-4 p-4 bg-dark-100 rounded-lg hover:bg-dark-200 transition-colors text-left group"
                >
                  <div className="w-10 h-10 rounded-lg bg-accent-blue/10 flex items-center justify-center flex-shrink-0 group-hover:bg-accent-blue/20 transition-colors">
                    <Icon className="w-5 h-5 text-accent-blue" />
                  </div>
                  <div>
                    <p className="text-white font-medium group-hover:text-accent-blue transition-colors">{question}</p>
                    <p className="text-sm text-gray-400 mt-1">{description}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex items-start gap-4 ${
                message.role === 'user' ? 'flex-row-reverse' : ''
              }`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                message.role === 'user'
                  ? 'bg-accent-blue'
                  : 'bg-dark-200'
              }`}>
                {message.role === 'user' ? (
                  <User className="w-5 h-5 text-white" />
                ) : (
                  <Bot className="w-5 h-5 text-accent-blue" />
                )}
              </div>
              <div className={`flex-1 rounded-lg p-4 ${
                message.role === 'user'
                  ? 'bg-accent-blue text-white'
                  : 'bg-dark-100 text-gray-200'
              }`}>
                <p className="whitespace-pre-wrap">{message.content}</p>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-lg bg-dark-200 flex items-center justify-center">
                <Bot className="w-5 h-5 text-accent-blue" />
              </div>
              <div className="flex-1 rounded-lg p-4 bg-dark-100">
                <Loader2 className="w-5 h-5 text-accent-blue animate-spin" />
              </div>
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 text-red-400 bg-red-900/20 p-4 rounded-lg">
              <AlertCircle className="w-5 h-5" />
              <p>{error}</p>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSubmit} className="p-4 border-t border-dark-200">
          <div className="flex gap-4">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Posez votre question..."
              className="flex-1"
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={isLoading || !input.trim()}
              className="px-4 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Send className="w-5 h-5" />
              Envoyer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Assistant;